import { MouseEvent, useContext, useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { Bottom, Fit, Top } from 'types/fit'
import Toggle from '@components/ui/Toggle'
import useLocalStorage from '@helpers/hooks/useLocalStorage'
import { FilterOption } from 'types/filters'
import { BUILD } from '@consts/index'
import CollectionContext from '@components/collection/collectionContext'

type ToggleProps = {
  isToggled: boolean
  onToggle: (ev: MouseEvent<HTMLButtonElement>) => void
}

type SavedFitBoxHeaderProps = ToggleProps & {
  onViewFitBox: () => void
  isFitOpen: boolean
}

const SavedFitBoxHeader = ({
  isToggled,
  onViewFitBox,
  onToggle,
  isFitOpen
}: SavedFitBoxHeaderProps) => {
  return (
    <div className={styles.SavedFitBoxHeader}>
      <Toggle
        toggled={isToggled}
        onToggle={onToggle}
        labelA="Apply your Fit"
        labelB="Your Fit"
      />

      <button
        className={`${styles.SavedFitBoxHeader__button} ${
          isToggled ? styles.SavedFitBoxHeader__button__on : ''
        }`}
        onClick={onViewFitBox}
      >
        {isFitOpen ? 'Close' : 'View'}
      </button>
    </div>
  )
}

function getFitOptionsByName(names: (keyof Fit)[], savedFit: Partial<Fit>) {
  return names.map(option => {
    return {
      name: option,
      value: savedFit[option]
    }
  })
}

type SavedFitBoxSizeOptionsProps = {
  category:
    | {
        type: string
        options: {
          name: keyof Top | keyof Bottom
          value: string | undefined
        }[]
      }
    | undefined
  isApplied: boolean
}

const SavedFitBoxSizeOptions = ({
  category,
  isApplied = false
}: SavedFitBoxSizeOptionsProps) => {
  const { type, options } = category || {}

  if (!options) return null

  return (
    <div className={styles.SavedFitBoxContent__categories}>
      <span
        className={`${styles.SavedFitBoxContent__title} ${
          isApplied ? styles.SavedFitBoxContent__title__isApplied : ''
        }`}
      >
        {type}
      </span>
      <div
        className={`${styles.SavedFitBoxContent__options} ${
          isApplied ? styles.SavedFitBoxContent__options__isApplied : ''
        }`}
      >
        {options.map(option => (
          <div className={styles.SavedFitBoxContent__option} key={option.name}>
            <span className={styles.SavedFitBoxContent__optionType}>
              {option.name}
            </span>
            <span className={styles.SavedFitBoxContent__optionValue}>
              {option.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export const SavedFitBoxContent = ({
  savedFit,
  isApplied = false
}: {
  savedFit: Partial<Fit>
  isApplied?: boolean
}) => {
  const savedFitCategories = [
    {
      type: 'Bottoms',
      options: getFitOptionsByName(['waist', 'build', 'leg'], savedFit)
    },
    {
      type: 'Tops',
      options: getFitOptionsByName(['size', 'cut', 'length'], savedFit)
    }
  ]

  return (
    <div
      className={`${styles.SavedFitBoxContent} ${
        isApplied ? styles.SavedFitBoxContent__isApplied : ''
      }`}
    >
      {savedFitCategories.map(category => (
        <SavedFitBoxSizeOptions
          category={category}
          key={category.type}
          isApplied={isApplied}
        />
      ))}
    </div>
  )
}

type SavedFitBoxProps = {
  savedFit: Partial<Fit>
  onApplyFitToggle: (state: boolean) => void
  selectedOptions: FilterOption[]
}

const EXCLUDE_SELECTED_OPTIONS = ['shortsleg', 'swimshortsleg']

const MIN_SAVED_FIT_OPTIONS = 2

export function SavedFitBox({
  savedFit,
  selectedOptions,
  onApplyFitToggle
}: SavedFitBoxProps) {
  const { setIsApplyFitActive } = useContext(CollectionContext)

  const [isSavedFitApplied, setIsSavedFitApplied] = useLocalStorage(
    'plp_filters_saved_fit_applied',
    false
  )

  const filteredSelectedOptions = selectedOptions.filter(
    option => !EXCLUDE_SELECTED_OPTIONS.includes(option.type)
  )

  const isSavedFitValid =
    filteredSelectedOptions.length > MIN_SAVED_FIT_OPTIONS &&
    filteredSelectedOptions.every(
      option =>
        savedFit[option.type as keyof Fit] ===
        String(option.value.replace(BUILD, '').trim())
    )

  const [isToggled, setIsToggled] = useState(isSavedFitApplied)
  const [isFitOpen, setIsFitOpen] = useState(false)

  const hasNoSavedFit = !Object.keys(savedFit).length

  const handleOnToggle = () => {
    onApplyFitToggle(!isToggled)
    setIsSavedFitApplied(!isToggled)
    setIsToggled(currentState => !currentState)
  }

  const handleOnViewFitBox = () => setIsFitOpen(prev => !prev)

  useEffect(() => {
    setIsSavedFitApplied(!!isSavedFitValid)
    setIsApplyFitActive(isSavedFitValid && isSavedFitApplied)
    setIsToggled(isSavedFitValid && isSavedFitApplied)
  }, [isSavedFitApplied, isSavedFitValid])

  if (hasNoSavedFit) return <></>

  return (
    <div
      className={`${styles.SavedFitBoxContainer} ${
        isToggled ? styles.SavedFitBoxContainer__on : ''
      }`}
    >
      <SavedFitBoxHeader
        onToggle={handleOnToggle}
        isToggled={isToggled}
        onViewFitBox={handleOnViewFitBox}
        isFitOpen={isFitOpen}
      />
      {isFitOpen ? (
        <SavedFitBoxContent savedFit={savedFit} isApplied={isToggled} />
      ) : null}
    </div>
  )
}

export default SavedFitBox
